import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  signal,
} from "@angular/core";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import {
  AlertPriority,
  AlertStatus,
  AlertType,
} from "../../interfaces/alert-type.enum";
import { Alert } from "../../interfaces/alert.interface";
import { AlertService } from "../../services/alert.service";

@Component({
  selector: "app-alert",
  imports: [CommonModule, ButtonModule, TranslocoDirective],
  templateUrl: "./alert.component.html",
  styleUrl: "./alert.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertComponent {
  readonly viewStyle = input<"small" | "default">("default");
  readonly alert = input.required<Alert>();
  readonly dismissed = output<void>();

  protected isDismissing = false;
  protected isExpanded = signal(false);

  protected readonly AlertPriority = AlertPriority;
  protected readonly AlertType = AlertType;
  protected readonly AlertStatus = AlertStatus;

  constructor(
    private alertService: AlertService,
    private messageService: MessageService,
    private tLoco: TranslocoService,
  ) {}

  dismiss(): void {
    if (this.isDismissing) return;

    this.isDismissing = true;
    this.alertService.dismissAlert(this.alert().id).subscribe({
      next: () => {
        this.dismissed.emit();
      },
      error: (error) => {
        console.error("Failed to dismiss alert:", error);
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: this.tLoco.translate("alert.failedToDismissAlert"),
        });
        this.isDismissing = false;
      },
    });
  }

  toggleExpanded(event: Event): void {
    event.stopPropagation();
    this.isExpanded.update((expanded) => !expanded);
  }

  getAlertIcon(alertType: AlertType): string {
    switch (alertType) {
      case AlertType.LABOR_BUDGET_EXCEEDED:
      case AlertType.LABOR_HOURS_AT_80_PERCENT:
        return "pi pi-clock";
      case AlertType.INVOICE_OVERDUE:
      case AlertType.INVOICE_OVERDUE_1_WEEK:
      case AlertType.INVOICE_OVERDUE_2_WEEKS:
      case AlertType.INVOICE_OVERDUE_1_MONTH:
      case AlertType.INVOICE_OVERDUE_2_MONTHS:
      case AlertType.INVOICE_OVERDUE_3_MONTHS:
        return "pi pi-receipt";
      case AlertType.PAY_APP_OVERDUE:
      case AlertType.PAY_APP_OVERDUE_1_WEEK:
      case AlertType.PAY_APP_OVERDUE_2_WEEKS:
      case AlertType.PAY_APP_OVERDUE_1_MONTH:
      case AlertType.PAY_APP_OVERDUE_2_MONTHS:
      case AlertType.PAY_APP_OVERDUE_3_MONTHS:
        return "pi pi-dollar";
      default:
        return "pi pi-bell";
    }
  }

  getAlertBgColor(priority: AlertPriority, alertStatus: AlertStatus): string {
    if (alertStatus === AlertStatus.DISMISSED) {
      return "t-bg-slate-50";
    }
    switch (priority) {
      case AlertPriority.CRITICAL:
        return "t-bg-red-50";
      case AlertPriority.HIGH:
        return "t-bg-orange-50";
      case AlertPriority.MEDIUM:
        return "t-bg-yellow-50";
      case AlertPriority.LOW:
        return "t-bg-blue-50";
      case AlertPriority.INFO:
        return "t-bg-slate-50";
      default:
        return "t-bg-slate-50";
    }
  }

  getAlertTextColor(priority: AlertPriority, alertStatus: AlertStatus): string {
    if (alertStatus === AlertStatus.DISMISSED) {
      return "t-text-slate-700";
    }
    switch (priority) {
      case AlertPriority.CRITICAL:
        return "t-text-red-700";
      case AlertPriority.HIGH:
        return "t-text-orange-700";
      case AlertPriority.MEDIUM:
        return "t-text-yellow-700";
      case AlertPriority.LOW:
        return "t-text-blue-700";
      case AlertPriority.INFO:
        return "t-text-slate-700";
      default:
        return "t-text-slate-700";
    }
  }

  getAlertIconColor(priority: AlertPriority, alertStatus: AlertStatus): string {
    if (alertStatus === AlertStatus.DISMISSED) {
      return "t-text-slate-500";
    }
    switch (priority) {
      case AlertPriority.CRITICAL:
        return "t-text-red-500";
      case AlertPriority.HIGH:
        return "t-text-orange-500";
      case AlertPriority.MEDIUM:
        return "t-text-yellow-600";
      case AlertPriority.LOW:
        return "t-text-blue-500";
      case AlertPriority.INFO:
        return "t-text-slate-500";
      default:
        return "t-text-slate-500";
    }
  }

  getAlertBorderColor(
    priority: AlertPriority,
    alertStatus: AlertStatus,
  ): string {
    if (alertStatus === AlertStatus.DISMISSED) {
      return "t-border-slate-200";
    }
    switch (priority) {
      case AlertPriority.CRITICAL:
        return "t-border-red-200";
      case AlertPriority.HIGH:
        return "t-border-orange-200";
      case AlertPriority.MEDIUM:
        return "t-border-yellow-200";
      case AlertPriority.LOW:
        return "t-border-blue-200";
      case AlertPriority.INFO:
        return "t-border-slate-200";
      default:
        return "t-border-slate-200";
    }
  }
}
